// frontend/src/components/charts/ShortageTable.jsx
// Compact AG-Grid table showing at-risk items (stockout risk).
// Embedded in the Home page dashboard tile (half-width).
// Lists the rows of the full Stockout Report that have a default supplier
// and still need an order once what is on its way has landed, most urgent
// first, each with the report's own Days of cover, and offers one thing to
// do about them: create the purchase order from the recommendation that
// orders that item at that location first, in a popup over Home.
//
// FINAL. Do not add columns, states or actions here — raise the idea with
// the user.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { dashboardColDef, dashboardGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn } from '../../utils/gridDefaults';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { usePurchaseOrderCreated } from '../../hooks/usePurchaseOrderCreated';
import { apiJson, translateRequestError } from '../../queries/api';
import { showToast } from '../common/appToastBus';
import PurchaseOrderForm from '../purchase-orders/PurchaseOrderForm';
import { recommendationRowActions, ROW_PRIMARY } from '../../pages/recommendationRowActions';
import { useIsShopify, itemIdentityColumns } from '../grid/productIdentity';
import { daysOfCoverColDef } from '../grid/daysOfCoverColumn';
import { noForecast, notSynchronized } from '../../utils/kpiValueCoverage';
import '../../styles/grid-page.css';

// The pending recommendation that orders the row's item at its location
// first, or null when none does. A pair can sit in several: the engine plans
// orders across a horizon, one recommendation per supplier, location and
// order date (purchase_orders_engine.pipeline.batch_recommendations).
async function firstPendingRecommendation({ item_code, location_code }) {
  const query = new URLSearchParams({
    status: 'pending',
    item_code,
    location_code,
    ordering: 'RecommendedOrderDate',
    page_size: '1',
  });
  const { results } = await apiJson.get(`/api/po-calculations/recommendations/?${query}`);
  return results.length ? results[0] : null;
}

// `catalogImporting` is `utils/syncGate.catalogImporting`: with it and its own
// payload's `total_items` the grid asks the Stockout risk tile's sync question
// (`utils/kpiValueCoverage.notSynchronized`), and while the catalog has not
// arrived, or is still arriving, says so in place of any rows.
//
// `forecastRun` is `utils/syncGate.forecastRunState`: with no forecast yet,
// the empty grid tells the same three cases apart as the Stockout risk tile
// above it (`utils/kpiValueCoverage.noForecast`).
//
// `hasForecastDemand` is the reorder-levels status flag (undefined until that
// read lands): a store whose forecasts exist and are all zero lists nothing
// because it has no sales yet, and the empty state offers
// `onOpenReorderLevels` instead of saying nothing is at risk.
function ShortageTable({
  useItemsData, catalogImporting, forecastRun, hasForecastDemand, onOpenReorderLevels,
}) {
  const { t } = useTranslation();
  const columnTypes = useColumnTypes();
  const { formatNumber } = useFormatters();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const { data, loading, error } = useItemsData();
  const onPurchaseOrderCreated = usePurchaseOrderCreated();
  // The recommendation the purchase order popup is open for, else null.
  const [orderRecId, setOrderRecId] = useState(null);

  // Opens the popup only for a recommendation the page itself offers
  // "Review & create" on (recommendationRowActions); otherwise says why not.
  const takeAction = useCallback(async (row) => {
    let recommendation;
    try {
      recommendation = await firstPendingRecommendation(row);
    } catch (err) {
      console.error('ShortageTable: failed to read the recommendation for %s at %s:', row.item_code, row.location_code, err);
      showToast(translateRequestError(err, t, 'home.shortage.openOrderFailed'), 'error');
      return;
    }
    if (recommendation === null) {
      showToast(t('home.shortage.noRecommendation', {
          button: t('purchaseRecommendations.actions.recalculate'),
          page: t('nav.createPurchaseOrders'),
        }), 'info');
      return;
    }
    if (recommendationRowActions(recommendation.erp_order_block).primary !== ROW_PRIMARY.CREATE_PO) {
      showToast(t('home.shortage.orderBlocked'), 'warning');
      return;
    }
    setOrderRecId(recommendation.PORecommendationID);
  }, [t]);

  // Stable identity — see ForecastGrid's getRowId for the AG Grid rationale.
  const getRowId = useCallback(
    (params) => `${params.data.item_id}-${params.data.location_id}`,
    [],
  );

  const columnDefs = useMemo(() => [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('home.shortage.product'),
      // Below productColumn's floor: in a half-width tile that floor pushes
      // the action button out of view, and the name and SKU cut off cleanly.
      product: flexColumn(2, 160),
      codeHeader: t('home.shortage.itemCode'),
      code: {
        tooltipField: 'item_code',
      },
      nameHeader: t('home.shortage.itemName'),
      name: {
        tooltipField: 'item_name',
        ...flexColumn(2),
      },
    }),
    {
      field: 'location_name',
      headerName: t('home.shortage.location'),
      tooltipField: 'location_name',
      ...flexColumn(),
      minWidth: 100,
    },
    {
      field: 'on_hand',
      headerName: t('home.shortage.onHand'),
      type: 'numberColumn',
    },
    {
      ...daysOfCoverColDef({ t, formatNumber }),
      // A pair with no figure has stock and no sales; of the levels this card
      // lists (reporting.ingredients.risk_priority_expr), only Below minimum
      // stock takes such a pair, so the dash says why it is here.
      tooltipValueGetter: (params) => (
        params.data.days_of_cover === null ? t('home.shortage.belowMinimumNoSales') : ''
      ),
    },
    actionColumn({
      cellRenderer: (params) => (
        <Button
          className="grid-action-btn"
          variant="contained"
          onMouseDown={e => e.stopPropagation()}
          onClick={() => takeAction(params.data)}
        >
          {t('home.shortage.takeAction')}
        </Button>
      ),
    }),
  ], [t, formatNumber, isShopify, takeAction]);

  // The popup is rendered beside these states, not inside the grid: the list
  // polls (useShortageItems), and a poll that empties it must not close an
  // order the merchant is writing.
  function shortageGrid() {
    if (loading) return <div className="dashboard-grid-wrapper"><div className="chart-state"><CircularProgress size={36} /></div></div>;
    if (error) return <div className="dashboard-grid-wrapper"><div className="chart-state"><Typography color="error">{error}</Typography></div></div>;
    if (notSynchronized(data.total_items, catalogImporting)) return (
      <div className="dashboard-grid-wrapper">
        <div className="chart-state">
          <Typography color="textSecondary">{t('home.shortage.notSynchronized')}</Typography>
        </div>
      </div>
    );
    if (data.items.length === 0 && data.has_forecasts && hasForecastDemand === false) return (
      <div className="dashboard-grid-wrapper">
        <div className="chart-state chart-state--action">
          <Typography color="textSecondary">{t('home.shortage.noSalesYet')}</Typography>
          <Button size="small" variant="outlined" onClick={onOpenReorderLevels}>
            {t('reorderLevels.title')}
          </Button>
        </div>
      </div>
    );
    if (data.items.length === 0) return (
      <div className="dashboard-grid-wrapper">
        <div className="chart-state">
          <Typography color="textSecondary">
            {data.has_forecasts ? t('home.shortage.noData') : {
              noForecast: () => t('home.shortage.noForecast'),
              forecastCalculating: () => t('home.metrics.forecastCalculating'),
              forecastFailed: () => t('home.metrics.forecastFailed'),
            }[noForecast(forecastRun).state]()}
          </Typography>
        </div>
      </div>
    );
    return (
      <div className="dashboard-grid-wrapper">
        {gridTheme && (
          <AgGridReact
            {...dashboardGridProps}
            theme={gridTheme}
            rowData={data.items}
            columnDefs={columnDefs}
            columnTypes={columnTypes}
            defaultColDef={dashboardColDef}
            getRowId={getRowId}
            getContextMenuItems={copyOnlyContextMenuItems}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {shortageGrid()}
      {orderRecId !== null && (
        <PurchaseOrderForm
          open
          onClose={() => setOrderRecId(null)}
          onSaved={onPurchaseOrderCreated}
          recommendationId={orderRecId}
        />
      )}
    </>
  );
}

export default ShortageTable;
